import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import { useFeather } from '../shell/useFeather';
import { AffiliatePropagaceSection, fmtSocialValue } from './AffiliatePropagaceSection';
import { COUNTRIES_TOP, COUNTRIES_REST, countryName } from '../shell/countries';
import {
  getAffilmasterAffiliates,
  getAffilmasterAffiliateDetail,
  getAffiliateSocials,
  refreshAffiliateSocials,
  type AffiliateSocials,
  type AffiliateSocialProfile,
  getAffilmasterContacts,
  addAffilmasterContact,
  updateAffilmasterContact,
  deleteAffilmasterContact,
  getAffilmasterAffiliateNotes,
  addAffilmasterAffiliateNote,
  updateAffilmasterAffiliateNote,
  deleteAffilmasterAffiliateNote,
  updateAffilmasterPrimaryContact,
  updateAffilmasterAffiliateProfile,
  deleteAffilmasterAffiliate,
  lookupAresByIco,
  addAffilmasterAffiliate,
  getAffilmasterAffiliateCampaignChart,
  getAffilmasterAffiliateMonthBreakdown,
  AffilmasterApiError,
  type AffiliateListItem,
  type AffiliateMonthly,
  type AffiliateUdaje,
  type AffiliateContact,
  type Readiness,
  type Reporting,
  getAffilmasterAffiliateInvoices,
  type AffiliateInvoice,
  pairAffilmasterAffiliateWithPap,
  type VipAccount,
} from '../api/affilmaster';
import { affiliateName, fmtN, summarizeNotReady, mesicLabel, fmtDen, fmtCastka } from './affilmaster-format';
import { CampaignChartModal, MonthBreakdownModal } from './AffiliateDrilldownModals';
import { NgrRoiCard } from './NgrRoiCard';
import './affilmaster-detail.css';
import { AffilmasterContactDirectory, type AffilmasterContactDirectoryItem } from './AffilmasterContactDirectory';
import { MailStatusMark } from '../mailing/MailingSubscriptionBadge';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { Button } from '../components/arc/button/button';
import { ArcPage, ArcPanel, ArcTableFrame, ArcTabs } from '../components/arc/primitives/arc-primitives';
import '../klienti/klienti.css';
import { MailingGroupsField, saveMailingGroups, type MailingGroupsValue } from '../mailing/MailingGroupsField';
import { HelpdeskContactTickets } from '../helpdesk/HelpdeskContactTickets';
import { confirmDialog, noticeDialog } from '../shell/dialogs';
import { AffilmasterNotesSection } from './AffilmasterNotesSection';
import { AffilmasterDetailMetricSection } from './AffilmasterDetailMetricSection';
import { AffilmasterDetailField, AffilmasterDetailFieldGroup, AffilmasterDetailSectionHeader } from './AffilmasterDetailFields';

const MARKETS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Všechny' },
  { id: 'cz', label: 'Jen CZ' },
  { id: 'sk', label: 'Jen SK' },
  { id: 'czsk', label: 'CZ + SK' },
  { id: 'none', label: 'Bez kampaní' },
];

// Trh = markets (PaP data10, "CZ,SK"), NE country (data5 = sídlo). Multi-hodnota.
function parseMarkets(m: string | null): string[] {
  return (m ?? '').split(/[;,/| ]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
}
function hasCzSk(markets: string[]): boolean {
  return markets.includes('CZ') || markets.includes('SK');
}
function isActiveStatus(status: string | null): boolean {
  return ['a', 'active', 'approved', '1'].includes((status ?? '').trim().toLowerCase());
}
function marketMatch(markets: string[], filter: string): boolean {
  const cz = markets.includes('CZ');
  const sk = markets.includes('SK');
  switch (filter) {
    case 'cz': return cz && !sk;
    case 'sk': return sk && !cz;
    case 'czsk': return cz && sk;
    case 'none': return !cz && !sk;
    default: return true;
  }
}

// Segmenty (1:1 prod filter). Segmentová klasifikace affiliatů zatím není v P1
// datech → segmentKey vrací 'none' ("Bez segmentu"); až přibude affilmaster
// segmentace, sem se napojí a-segment.
const SEGMENTS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Všechny segmenty' },
  { id: 'none', label: 'Bez segmentu' },
  { id: 'vipcz', label: 'VIP CZ' },
  { id: 'vipsk', label: 'VIP SK' },
  { id: 'neaktivsk', label: 'Neaktiv SK' },
  { id: 'neaktivcz', label: 'Neaktiv CZ' },
];
function segmentKey(_a: AffiliateListItem): string { return 'none'; }

function webHost(url: string | null): string | null {
  if (!url) return null;
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
  catch { return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, ''); }
}

function normalizedContactLabel(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase();
}

function contactCellLabel(a: AffiliateListItem): string {
  const primary = a.primary_contact_name?.trim();
  if (primary) return primary;
  const display = a.display_name?.trim();
  const company = affiliateName(a).trim();
  if (display && display !== company) return display;
  return '-';
}

function affiliatePersonName(a: AffiliateUdaje | null, companyLabel: string): string | null {
  if (!a) return null;
  if (a.contact_override_name?.trim()) return a.contact_override_name.trim();
  const companyNames = [companyLabel, a.company_name, a.display_name]
    .map(normalizedContactLabel)
    .filter(Boolean);
  const isCompanyName = (candidate: string): boolean => companyNames.includes(normalizedContactLabel(candidate));

  const explicitName = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  if (explicitName && !isCompanyName(explicitName)) return explicitName;

  const contactPerson = a.contact_person?.trim() ?? '';
  if (!contactPerson) return null;
  return isCompanyName(contactPerson) ? null : contactPerson;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; items: AffiliateListItem[]; freshness: Readiness }
  | { kind: 'not-ready'; freshness: Readiness };


interface AffiliatesPageProps {
  detailId?: string;
  onOpenDetail?: (id: string) => void;
  onCloseDetail?: () => void;
}

export function AffilmasterAffiliatesPage({ detailId, onOpenDetail, onCloseDetail }: AffiliatesPageProps = {}) {
  const [market, setMarket] = useState('all');
  const [segment, setSegment] = useState('all');
  const [onlyActive, setOnlyActive] = useState(false);
  const [query, setQuery] = useState('');
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [localSel, setLocalSel] = useState<AffiliateListItem | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const openDetail = (a: AffiliateListItem) => { if (onOpenDetail) onOpenDetail(a.pap_affiliate_id); else setLocalSel(a); };
  const closeDetail = () => { if (onCloseDetail) onCloseDetail(); else setLocalSel(null); };

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    getAffilmasterAffiliates(365)
      .then((res: Reporting<AffiliateListItem[]>) => {
        if (cancelled) return;
        if (res.ok) setState({ kind: 'ready', items: res.data, freshness: res.freshness });
        else setState({ kind: 'not-ready', freshness: res.freshness });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: e instanceof AffilmasterApiError ? e.message : 'Načtení Affilmaster dat selhalo' });
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Seznam = jen affiliaty s definovaným CZ/SK trhem (jako prod), globální/bez
  // trhu se nezobrazují (Dan: každý partner má lokaci CZ/SK).
  const base = useMemo(
    () => (state.kind === 'ready' ? state.items.filter((a) => hasCzSk(parseMarkets(a.markets))) : []),
    [state],
  );
  const total = base.length;
  const activeTotal = useMemo(() => base.filter((a) => isActiveStatus(a.status)).length, [base]);
  const czTotal = useMemo(() => base.filter((a) => parseMarkets(a.markets).includes('CZ')).length, [base]);
  const skTotal = useMemo(() => base.filter((a) => parseMarkets(a.markets).includes('SK')).length, [base]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return base.filter((a) => {
      if (!marketMatch(parseMarkets(a.markets), market)) return false;
      if (segment !== 'all' && segmentKey(a) !== segment) return false;
      if (onlyActive && !isActiveStatus(a.status)) return false;
      if (q) {
        const hay = `${a.company_name ?? ''} ${a.display_name ?? ''} ${a.website ?? ''} ${a.email ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [base, market, segment, onlyActive, query]);
  const visible = filtered;
  const selected = useMemo(
    () => (detailId && state.kind === 'ready' ? state.items.find((a) => a.pap_affiliate_id === detailId) ?? null : localSel),
    [detailId, state, localSel],
  );

  useFeather([state, selected, market, segment, onlyActive, query]);

  if (selected) {
    return (
      <AffiliateDetailView
        row={selected}
        onBack={closeDetail}
        onDeleted={() => { closeDetail(); setReloadKey((k) => k + 1); }}
        onPaired={(newId) => {
          setReloadKey((k) => k + 1);
          if (onOpenDetail) onOpenDetail(newId);
          else setLocalSel(null);
        }}
      />
    );
  }

  return (
    <>
      {state.kind === 'loading' && (
        <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>
      )}

      {state.kind === 'error' && <div className="alert alert-danger" role="alert">{state.message}</div>}

      {state.kind === 'not-ready' && <NotReadyBanner freshness={state.freshness} />}

      {state.kind === 'ready' && (
        <>
          {!state.freshness.ready && <StaleStrip freshness={state.freshness} />}
          <div className="prototype-page-head">
            <div><div className="prototype-eyebrow">AFFILMASTER</div><h1>Affiliates</h1><p>Přehled affiliate partnerů, kontaktů a jejich stavu</p></div>
            <button type="button" className="btn btn-primary prototype-primary-btn" onClick={() => setShowAdd((v) => !v)}>
              <i data-feather="plus" /> Přidat Affiliate
            </button>
          </div>

          {showAdd && (
            <AddAffiliateForm
              onCancel={() => setShowAdd(false)}
              onSaved={() => { setShowAdd(false); setReloadKey((k) => k + 1); }}
            />
          )}

          <div className="prototype-summary-row">
            <SummaryMetric label="Celkem affiliates" value={total} />
            <SummaryMetric label="Aktivní" value={activeTotal} detail={total ? `${pct1((activeTotal / total) * 100)} % z celku` : undefined} />
            <SummaryMetric label="Český trh" value={czTotal} detail={total ? `${pct1((czTotal / total) * 100)} % z celku` : undefined} />
            <SummaryMetric label="Slovenský trh" value={skTotal} detail={total ? `${pct1((skTotal / total) * 100)} % z celku` : undefined} />
          </div>

          <div className="prototype-list-toolbar">
              <div className="prototype-list-filters">
                <ToolbarSelect label="Trh" value={market} onChange={setMarket} options={MARKETS} />
                <ToolbarSelect label="Segment" value={segment} onChange={setSegment} options={SEGMENTS} />
                <label className="prototype-switch-wrap" htmlFor="aff-only-active">
                  <input type="checkbox" role="switch" id="aff-only-active"
                    checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
                  <span className="prototype-switch" />Jen aktivní
                </label>
                <button type="button" className="prototype-clear-btn"
                  onClick={() => { setMarket('all'); setSegment('all'); setOnlyActive(false); setQuery(''); }}>
                  vyčistit
                </button>
              </div>
              <label className="prototype-list-search">
                <i data-feather="search" aria-hidden="true" />
                <input type="search" placeholder="Hledat firmu, kontakt, web nebo e-mail…" value={query} onChange={(e) => setQuery(e.target.value)} />
              </label>
          </div>

          <div className="card shadow-sm">
            <div className="aff-list-tablewrap">
              <div className="table-responsive-mobile">
              <table className="table table-hover align-middle aff-list-table mb-0">
                <thead>
                  <tr>
                    <th className="ps-3">Firma / Affiliate</th>
                    <th>Zobrazovaný název</th>
                    <th>Kontakt</th>
                    <th>Web</th>
                    <th className="text-center">Segment</th>
                    <th className="text-center">Trh</th>
                    <th className="text-center">Stav</th>
                    <th className="text-end pe-3" />
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-muted py-4">Žádní affiliate.</td></tr>
                  )}
                  {visible.map((a) => (
                    <tr key={a.pap_affiliate_id} className="aff-row" onClick={() => openDetail(a)}>
                      <td className="ps-3"><div className="prototype-company-cell"><span className="prototype-company-avatar">{initials(affiliateName(a))}</span><div><strong>{affiliateName(a)}</strong>{a.vip_partner && <VipBadge />}</div></div></td>
                      <td><div className="prototype-contact-cell">{a.display_name?.trim() || '-'}</div></td>
                      <td><div className="prototype-contact-cell"><strong>{contactCellLabel(a)}</strong></div></td>
                      <td>{(() => { const first = parseWebsites(a.website)[0]; return <WebCell url={first?.url ?? null} kind={first?.kind} />; })()}</td>
                      <td className="aff-segment-cell"><SegmentBadges /></td>
                      <td className="aff-country-cell"><MarketBadges markets={a.markets} /></td>
                      <td className="text-center"><span className={`prototype-status ${isActiveStatus(a.status) ? 'is-active' : 'is-paused'}`}>{isActiveStatus(a.status) ? 'Aktivní' : 'Pozastavený'}</span></td>
                      <td className="prototype-row-arrow pe-3" aria-label="Detail affiliata"><span className="feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function SummaryMetric({ label, value, detail }: { label: string; value: number; detail?: string }) {
  return <article className="prototype-summary-metric"><span>{label}</span><strong>{fmtN(value)}</strong>{detail && <small>{detail}</small>}</article>;
}

type Opt = { id: string; label: string };

function ToolbarSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Opt[] }) {
  return (
    <div className="prototype-filter">
      <span>{label}</span>
      <CompactSelect
        value={value}
        options={options.map((option) => ({ value: option.id, label: option.label }))}
        onChange={onChange}
        ariaLabel={label}
      />
    </div>
  );
}

const WEBSITE_SEP = '|';
const MAX_WEBSITES = 8;

export type WebKind = 'web' | 'twitch' | 'youtube' | 'rumble' | 'instagram' | 'facebook' | 'tiktok' | 'kick' | 'telegram' | 'twitter';
export interface WebEntry { kind: WebKind; url: string }

const WEB_KINDS: Array<{ kind: WebKind; label: string; icon: string; cls: string; color: string; match?: RegExp }> = [
  { kind: 'web',       label: 'Web',       icon: 'globe',        cls: 'wb', color: '#4b5563' },
  { kind: 'twitch',    label: 'Twitch',    icon: 'twitch',       cls: 'tw', color: '#9146ff', match: /twitch\.tv/ },
  { kind: 'youtube',   label: 'YouTube',   icon: 'youtube',      cls: 'yt', color: '#ff0033', match: /youtube\.com|youtu\.be/ },
  { kind: 'rumble',    label: 'Rumble',    icon: 'rumble',       cls: 'rb', color: '#85c742', match: /rumble\.com/ },
  { kind: 'instagram', label: 'Instagram', icon: 'instagram',    cls: 'ig', color: '#e1306c', match: /instagram\.com/ },
  { kind: 'facebook',  label: 'Facebook',  icon: 'facebook',     cls: 'fb', color: '#1877f2', match: /facebook\.com|fb\.com/ },
  { kind: 'tiktok',    label: 'TikTok',    icon: 'tiktok',       cls: 'tt', color: '#010101', match: /tiktok\.com/ },
  { kind: 'kick',      label: 'Kick',      icon: 'kick',         cls: 'kc', color: '#1f8f1b', match: /kick\.com/ },
  { kind: 'telegram',  label: 'Telegram',  icon: 'telegram',     cls: 'tg', color: '#229ed9', match: /t\.me|telegram\.me|telegram\.org/ },
  { kind: 'twitter',   label: 'Twitter',   icon: 'twitter',      cls: 'tx', color: '#1da1f2', match: /twitter\.com|x\.com/ },
];
export const WEB_KIND_MAP = Object.fromEntries(WEB_KINDS.map((k) => [k.kind, k])) as Record<WebKind, typeof WEB_KINDS[number]>;

export function WebKindIcon({ kind, size = 16 }: { kind: WebKind; size?: number }) {
  const meta = WEB_KIND_MAP[kind] ?? WEB_KIND_MAP.web;
  const style = { width: size, height: size, color: meta.color, flex: '0 0 auto' } as const;
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'currentColor', 'aria-hidden': true, style };
  switch (kind) {
    case 'twitch':
      return <svg {...common}><path d="M4.286 0L1.143 3.143v17.714h5.714V24h3.143l3.143-3.143h4.571L23.143 15.43V0zm16.571 14.286l-3.428 3.428h-4.572l-3.143 3.143v-3.143H6.571V2.286h14.286zM15.429 6.286h-2.286v6.857h2.286zm-6.286 0H6.857v6.857h2.286z" /></svg>;
    case 'rumble':
      return <svg {...common}><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1.5 6.155l6.732 3.845-6.732 3.845z" /></svg>;
    case 'kick':
      return <svg {...common}><path d="M3 2h5v6h2V6h2V4h2V2h5v6h-2v2h-2v2h2v2h2v6h-5v-2h-2v-2h-2v2H8v2H3z" /></svg>;
    case 'tiktok':
      return <svg {...common}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.87a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.3z" /></svg>;
    case 'youtube':
      return <svg {...common}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>;
    case 'instagram':
      return <svg {...common}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg>;
    case 'facebook':
      return <svg {...common}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>;
    case 'telegram':
      return <svg {...common}><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.14.175-.33.258-.52.258-.33 0-.274-.123-.388-.44l-.883-2.908-2.956-.918c-.642-.198-.649-.642.136-.949l11.566-4.46c.532-.198.998.127.825.904z" /></svg>;
    case 'twitter':
      return <svg {...common}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
    default:
      return <svg {...common}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" /></svg>;
  }
}

function detectKindFromUrl(url: string): WebKind {
  const raw = String(url || '').toLowerCase();
  for (const k of WEB_KINDS) {
    if (k.match && k.match.test(raw)) return k.kind;
  }
  return 'web';
}

export function parseWebsites(raw: string | null | undefined): WebEntry[] {
  return String(raw ?? '')
    .split(WEBSITE_SEP)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_WEBSITES)
    .map((token) => {
      const m = token.match(/^([a-z]+):(.*)$/);
      if (m && (WEB_KIND_MAP as Record<string, unknown>)[m[1]!] !== undefined && !/^https?$/i.test(m[1]!)) {
        return { kind: m[1] as WebKind, url: (m[2] ?? '').trim() };
      }
      return { kind: detectKindFromUrl(token), url: token };
    });
}
export function serializeWebsites(list: WebEntry[]): string {
  return list
    .map((e) => ({ kind: e.kind, url: String(e.url ?? '').trim().slice(0, 90) }))
    .filter((e) => e.url)
    .slice(0, MAX_WEBSITES)
    .map((e) => `${e.kind}:${e.url}`)
    .join(WEBSITE_SEP);
}

export function webPlatform(url: string | null, kindHint?: WebKind): { kind: WebKind; name: string; full: string; href: string } | null {
  if (!url) return null;
  const raw = url.trim();
  const href = raw.startsWith('http') ? raw : `https://${raw}`;
  let host = '';
  try { host = new URL(href).hostname.replace(/^www\./, ''); }
  catch { host = raw.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, ''); }
  let full = raw;
  try { full = decodeURIComponent(raw); } catch { /* ponech raw */ }
  full = full.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const kind = kindHint ?? detectKindFromUrl(raw);
  const meta = WEB_KIND_MAP[kind];
  const name = kind !== 'web' && meta ? meta.label : host;
  return { kind, name, full, href };
}

export function WebCell({ url, kind }: { url: string | null; kind?: WebKind }) {
  const p = webPlatform(url, kind);
  if (!p) return <span className="text-muted">-</span>;
  return (
    <a className="aff-web-link" href={p.href} target="_blank" rel="noreferrer" title={p.full} onClick={(e) => e.stopPropagation()}>
      <WebKindIcon kind={p.kind} size={14} />
      <span className="aff-web-label">{p.name}</span>
    </a>
  );
}

export function WebStacked({ url, kind }: { url: string | null; kind?: WebKind }) {
  const p = webPlatform(url, kind);
  if (!p) return <span className="text-muted">-</span>;
  // Název platformy nese už popisek řádku vlevo, proto zbývá jen ikona a odkaz.
  return (
    <a className="aff-web-link" href={p.href} target="_blank" rel="noreferrer" title={p.full} onClick={(e) => e.stopPropagation()}>
      <WebKindIcon kind={p.kind} size={16} />
      <span className="aff-web-full">{p.full}</span>
    </a>
  );
}

function SegmentBadges() {
  return <span className="text-muted">-</span>;
}

function MarketBadges({ markets }: { markets: string | null }) {
  const ms = parseMarkets(markets).filter((m) => m === 'CZ' || m === 'SK');
  if (!ms.length) return <span className="text-muted">-</span>;
  return <>{ms.map((m) => <span key={m} className={`badge ${m === 'CZ' ? 'badge-cz' : 'badge-sk'}`}>{m}</span>)}</>;
}

function AddAffiliateForm({ onCancel, onSaved }: { onCancel: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: '', website: '', email: '', phone: '' });
  const [cz, setCz] = useState(true);
  const [sk, setSk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!f.name.trim()) { setErr('Název (Firma / Affiliate) je povinný'); return; }
    const markets = [cz ? 'CZ' : null, sk ? 'SK' : null].filter(Boolean) as string[];
    if (!markets.length) { setErr('Vyber alespoň jeden trh (CZ nebo SK)'); return; }
    setSaving(true); setErr(null);
    addAffilmasterAffiliate({ name: f.name.trim(), website: f.website || null, markets, email: f.email || null, phone: f.phone || null })
      .then(() => onSaved())
      .catch((e2: unknown) => setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Uložení affiliata selhalo'))
      .finally(() => setSaving(false));
  };
  return (
    <div className="prototype-modal-backdrop" onClick={onCancel}>
      <form className="card prototype-modal-card prototype-entity-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
        <div className="prototype-modal-head">
          <div><div className="prototype-eyebrow">AFFILIATE PARTNER</div><h2>Nový affiliate</h2><p>Základní údaje nového affiliate partnera</p></div>
          <button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={onCancel}>×</button>
        </div>
        <div className="card-body prototype-entity-form">
          {err && <div className="prototype-form-error">{err}</div>}
          <div className="prototype-field full"><label>Firma / Affiliate *</label><input className="form-control" autoFocus value={f.name} onChange={set('name')} /></div>
          <div className="prototype-field full"><label>Web</label><input className="form-control" placeholder="https://" value={f.website} onChange={set('website')} /></div>
          <div className="prototype-field"><label>E-mail</label><input className="form-control" type="email" value={f.email} onChange={set('email')} /></div>
          <div className="prototype-field"><label>Telefon</label><input className="form-control" value={f.phone} onChange={set('phone')} /></div>
          <fieldset className="prototype-market-picker full"><legend>Trh *</legend><label><input type="checkbox" checked={cz} onChange={(e) => setCz(e.target.checked)} /><span>CZ</span></label><label><input type="checkbox" checked={sk} onChange={(e) => setSk(e.target.checked)} /><span>SK</span></label></fieldset>
        </div>
        <div className="card-footer prototype-modal-actions"><button type="button" className="btn prototype-secondary-btn" onClick={onCancel}>Zrušit</button><button type="submit" className="btn btn-primary prototype-primary-btn" disabled={saving}>{saving ? 'Ukládám…' : 'Vytvořit affiliate'}</button></div>
      </form>
    </div>
  );
}

function NotReadyBanner({ freshness }: { freshness: Readiness }) {
  return (
    <div className="alert alert-warning" role="alert">
      <h6 className="alert-heading mb-1">
        <span className="feather-icon me-1"><i data-feather="alert-triangle" /></span>
        Affilmaster data nejsou aktuální
      </h6>
      <p className="mb-2 small">{summarizeNotReady(freshness)}</p>
      <FreshnessTable freshness={freshness} />
    </div>
  );
}

// Měkký „stale" proužek (Dan 2026-06-06): data EXISTUJÍ, jen nejsou čerstvá -
// zobraz je + nenápadné upozornění, NEschovávej je (na rozdíl od NotReadyBanner,
// který platí jen když není co ukázat).
function StaleStrip({ freshness }: { freshness: Readiness }) {
  const req = freshness.sources.find((s) => s.required) ?? freshness.sources[0];
  const ageH = req?.last_success_age_hours ?? null;
  const ageTxt = ageH == null ? null : ageH < 48 ? `${Math.round(ageH)} h` : `${Math.round(ageH / 24)} dní`;
  return (
    <div className="alert alert-warning py-2 px-3 mb-2 d-flex align-items-center gap-2 small" role="alert">
      <i data-feather="alert-triangle" style={{ width: 15, height: 15, flexShrink: 0 }} />
      <span>Data nemusí být aktuální{ageTxt ? `, poslední aktualizace před ${ageTxt}` : ''}. Zobrazujeme poslední dostupná; sync běží automaticky.</span>
    </div>
  );
}

function FreshnessTable({ freshness }: { freshness: Readiness }) {
  return (
    <div className="table-responsive-mobile">
    <table className="table table-sm mb-0 small">
      <thead>
        <tr><th>Zdroj</th><th>Stav</th><th>Poslední úspěch</th><th>Data k</th><th>Povinný</th></tr>
      </thead>
      <tbody>
        {freshness.sources.map((s) => (
          <tr key={s.source}>
            <td>{s.source}</td>
            <td>
              <span className={`badge ${s.status === 'ok' && s.fresh ? 'bg-success' : 'bg-danger'}`}>
                {s.status}{s.required && !s.fresh ? ' · stale' : ''}
              </span>
            </td>
            <td>{s.last_success_at ? `${Math.round(s.last_success_age_hours ?? 0)} h` : '-'}</td>
            <td>{s.data_through_date ?? '-'}</td>
            <td>{s.required ? 'ano' : 'ne'}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

type DetailState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: AffiliateMonthly; freshness: Readiness }
  | { kind: 'not-ready'; freshness: Readiness };

const SECTIONS = [
  { id: 'prehled', label: 'Přehledy' },
  { id: 'udaje', label: 'Údaje' },
  { id: 'kontakty', label: 'Kontakty' },
  { id: 'poznamky', label: 'Poznámky' },
  { id: 'propagace', label: 'Propagace' },
  { id: 'mesice', label: 'Měsíční rozpad' },
  { id: 'kampane', label: 'Kampaně' },
  { id: 'vip', label: 'VIP napojení' },
  { id: 'vyuctovani', label: 'Doklady' },
  { id: 'helpdesk', label: 'Helpdesk' },
] as const;

const CZ_MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase() || '?';
}
function czDate(s: string | null): string | null {
  if (!s) return null;
  const d = new Date(String(s).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;
  try { return new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' }).format(d); }
  catch { return null; }
}
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  const idx = Number(m) - 1;
  if (idx < 0 || idx >= 12) return ym;
  const nm = CZ_MONTHS[idx] ?? '';
  return `${nm.charAt(0).toUpperCase()}${nm.slice(1)} ${y}`;
}
function monthTableLabel(ym: string): string {
  const [year, month] = ym.split('-');
  return year && month ? `${month}/${year}` : ym;
}
function fmtMoney(v: number, currency: string): string {
  const s = new Intl.NumberFormat('cs-CZ').format(Math.round(v || 0));
  return currency === 'EUR' ? `${s} €` : `${s} Kč`;
}
function pct1(n: number): string { return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0); }
function roiPct(ngr: number, comm: number): string | null {
  if (!comm) return null;
  return new Intl.NumberFormat('cs-CZ').format(Math.round(((ngr - comm) / comm) * 100));
}
function roiColorHex(ngr: number, comm: number): string | undefined {
  if (!comm) return undefined;
  const roi = ((ngr - comm) / comm) * 100;
  return roi >= 100 ? '#34c759' : roi >= 0 ? '#ff9500' : '#c82b23';
}
function periodLabel(first: string | null, last: string | null): string {
  if (!first || !last) return '-';
  const f = (d: string) => { const [y, m] = d.split('-'); return `${String(Number(m)).padStart(2, '0')}/${(y ?? '').slice(2)}`; };
  return `${f(first)}–${f(last)}`;
}
function RowArrow({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <button type="button" className="prototype-row-arrow-btn" title={title} onClick={onClick} aria-label={title}>
      <span className="feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span>
    </button>
  );
}

type DrillModal = { kind: 'campaign'; cid: string; cname: string } | { kind: 'month'; ym: string } | null;

function AffiliateDetailView({ row, onBack, onDeleted, onPaired }: { row: AffiliateListItem; onBack: () => void; onDeleted?: () => void; onPaired?: (papAffiliateId: string) => void }) {
  const [section, setSection] = useState<string>('prehled');
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [modal, setModal] = useState<DrillModal>(null);
  const [detailReload, setDetailReload] = useState(0);
  const [contactCount, setContactCount] = useState<number>(0);
  const [profileForm, setProfileForm] = useState<null | { displayName:string; companyName:string; website:string; country:string; markets:string; status:string; ico:string; dic:string; billingStreet:string; billingCity:string; billingZip:string; billingCountry:string }>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [aresBusy, setAresBusy] = useState(false);
  const [aresMsg, setAresMsg] = useState<string | null>(null);
  const [socials, setSocials] = useState<AffiliateSocials | null>(null);
  const [socialsLoading, setSocialsLoading] = useState(false);
  const [socialsRefreshing, setSocialsRefreshing] = useState(false);
  const [socialsError, setSocialsError] = useState<string | null>(null);
  const [socialsFocus, setSocialsFocus] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSocials(null);
    setSocialsError(null);
    setSocialsLoading(true);
    getAffiliateSocials(row.pap_affiliate_id)
      .then(async (first) => {
        if (cancelled) return;
        setSocials(first);
        setSocialsLoading(false);
        if (!first.stale && first.profiles.length > 0) return;
        setSocialsRefreshing(true);
        try {
          const fresh = await refreshAffiliateSocials(row.pap_affiliate_id, false);
          if (!cancelled) setSocials(fresh);
        } catch (e: unknown) {
          if (!cancelled) setSocialsError(e instanceof AffilmasterApiError ? e.message : 'Aktualizace propagačních kanálů selhala');
        } finally {
          if (!cancelled) setSocialsRefreshing(false);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setSocialsLoading(false);
        setSocialsError(e instanceof AffilmasterApiError ? e.message : 'Načtení propagačních kanálů selhalo');
      });
    return () => { cancelled = true; };
  }, [row.pap_affiliate_id, detailReload]);

  const refreshSocials = () => {
    setSocialsRefreshing(true);
    setSocialsError(null);
    refreshAffiliateSocials(row.pap_affiliate_id, true)
      .then((fresh) => setSocials(fresh))
      .catch((e: unknown) => setSocialsError(e instanceof AffilmasterApiError ? e.message : 'Aktualizace propagačních kanálů selhala'))
      .finally(() => setSocialsRefreshing(false));
  };

  const openChannel = (profileId: number | null) => {
    setSocialsFocus(profileId);
    setSection('propagace');
  };

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    getAffilmasterAffiliateDetail(row.pap_affiliate_id)
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setState({ kind: 'ready', data: res.data, freshness: res.freshness });
        else setState({ kind: 'not-ready', freshness: res.freshness });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: e instanceof AffilmasterApiError ? e.message : 'Detail selhal' });
      });
    return () => { cancelled = true; };
  }, [row.pap_affiliate_id, detailReload]);

  useFeather([state, section, profileForm, profileSaving]);

  const months = state.kind === 'ready' ? state.data.months : [];
  const campaigns = state.kind === 'ready' ? state.data.campaigns : [];
  const currency = state.kind === 'ready' ? state.data.currency : 'EUR';
  const affiliate = state.kind === 'ready' ? state.data.affiliate : null;
  const name = affiliateName({
    company_name: affiliate?.company_name ?? row.company_name,
    display_name: affiliate?.display_name ?? row.display_name,
    pap_affiliate_id: row.pap_affiliate_id,
  });
  const country = affiliate?.country ?? row.country;
  const totals = useMemo(() => sumMonths(months), [months]);
  const heroRoi = totals.commission ? Math.round(((totals.ngr_volume - totals.commission) / totals.commission) * 100) : null;
  const person = affiliatePersonName(affiliate, name);
  const email = affiliate?.email ?? null;
  const web = affiliate?.website ?? null;
  const isManual = affiliate?.source === 'manual';
  const isVip = !!(affiliate?.vip_partner || row.vip_partner);
  const vipAccounts = state.kind === 'ready' ? (state.data.vip_accounts ?? []) : [];
  const activSince = czDate(affiliate?.approved_at ?? null);
  const lastAct = czDate(affiliate?.last_activity_at ?? null);

  useEffect(() => {
    let cancelled = false;
    setContactCount(0);
    getAffilmasterContacts(row.pap_affiliate_id)
      .then((rows) => { if (!cancelled) setContactCount(rows.length); })
      .catch(() => { if (!cancelled) setContactCount(0); });
    return () => { cancelled = true; };
  }, [row.pap_affiliate_id, detailReload]);
  const openProfile = () => {
    setProfileError(null); setAresMsg(null);
    setProfileForm({
      displayName:affiliate?.display_name ?? row.display_name ?? '', companyName:affiliate?.company_name ?? row.company_name ?? '',
      website:affiliate?.website ?? row.website ?? '', country:affiliate?.country ?? row.country ?? '',
      markets:affiliate?.markets ?? row.markets ?? '', status:isActiveStatus(affiliate?.status ?? row.status) ? 'active' : 'inactive',
      ico: affiliate?.ico ?? '', dic: affiliate?.dic ?? '',
      billingStreet: affiliate?.street ?? '', billingCity: affiliate?.city ?? '',
      billingZip: affiliate?.zipcode ?? '', billingCountry: (affiliate?.country ?? row.country ?? '').toUpperCase().slice(0,2),
    });
  };
  const saveProfile = async () => {
    if (!profileForm) return;
    setProfileSaving(true); setProfileError(null);
    try { await updateAffilmasterAffiliateProfile(row.pap_affiliate_id,profileForm); setProfileForm(null); setDetailReload((v)=>v+1); }
    catch (e) { setProfileError(e instanceof AffilmasterApiError ? e.message : 'Uložení profilu selhalo'); }
    finally { setProfileSaving(false); }
  };
  const loadFromAres = async () => {
    if (!profileForm) return;
    const ico = profileForm.ico.replace(/\D/g,'');
    if (!/^\d{6,8}$/.test(ico)) { setAresMsg('Vyplňte platné IČO (6–8 číslic)'); return; }
    setAresBusy(true); setAresMsg(null);
    try {
      const d = await lookupAresByIco(ico);
      setProfileForm({
        ...profileForm,
        ico: d.ico,
        dic: d.dic ?? profileForm.dic,
        companyName: d.companyName ?? profileForm.companyName,
        billingStreet: d.street ?? profileForm.billingStreet,
        billingCity: d.city ?? profileForm.billingCity,
        billingZip: d.zip ?? profileForm.billingZip,
        billingCountry: d.country || profileForm.billingCountry || 'CZ',
      });
      setAresMsg(`Načteno z ARES: ${d.companyName ?? d.ico}`);
    } catch (e) {
      setAresMsg(e instanceof AffilmasterApiError ? e.message : 'ARES: chyba načtení');
    } finally { setAresBusy(false); }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);
  const handleDelete = async () => {
    setMenuOpen(false);
    const label = affiliate?.display_name ?? row.display_name ?? row.pap_affiliate_id;
    if (!(await confirmDialog({ title: 'Smazat affiliate?', message: `„${label}“ zmizí ze seznamu; jeho historická data v přehledech zůstanou.`, confirmLabel: 'Smazat', danger: true }))) return;
    try {
      await deleteAffilmasterAffiliate(row.pap_affiliate_id);
      onDeleted?.();
    } catch (e) {
      void noticeDialog({ title: 'Smazání selhalo', message: e instanceof AffilmasterApiError ? e.message : undefined });
    }
  };

  return (
    <ArcPage as="div" className="affiliate-detail-view klient-detail-arc">
      <div className="klient-detail-back-row"><Button type="button" variant="secondary" size="sm" onClick={onBack}>Zpět na seznam</Button></div>

      <ArcPanel className="klient-detail-hero">
        <div className="klient-detail-hero-row">
          <div className="klient-detail-identity">
            <div className="klient-detail-avatar klient-detail-initials">{initials(name)}</div>
            <div className="klient-detail-identity-copy">
              <div className="klient-detail-eyebrow">Affiliate partner</div>
              <h1 className="klient-detail-name">{name}</h1>
              <div className="klient-detail-meta">
                <span>{isManual ? 'Bez PaP ID' : row.pap_affiliate_id}</span><i>•</i><span>{countryName(country) ?? '-'}</span>
                <span className={`prototype-status ${isActiveStatus(affiliate?.status ?? row.status) ? 'is-active' : 'is-paused'}`}>{isActiveStatus(affiliate?.status ?? row.status) ? 'Aktivní' : 'Pozastavený'}</span>
                {isVip && <VipBadge title="VIP partner s vlastním účtem v PaP instancích kasin" />}
              </div>
            </div>
          </div>
          <div className="klient-detail-actions">
            <Button type="button" variant="secondary" size="sm" aria-label="Další možnosti" title="Další možnosti" aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}><i data-feather="more-horizontal" /></Button>
            {menuOpen && (
              <div className="klient-detail-action-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                <Button type="button" variant="danger" size="sm" role="menuitem" onClick={handleDelete}>Smazat affiliate</Button>
              </div>
            )}
            <Button type="button" size="sm" disabled={!affiliate} onClick={openProfile}>Upravit</Button>
          </div>
        </div>
      </ArcPanel>

      {isManual && <PapPairingCard manualId={row.pap_affiliate_id} fromCrm={affiliate?.crm_lead_id != null} onPaired={onPaired} />}

      <ArcTabs label="Karty affiliate partnera" items={SECTIONS.filter((s) => s.id !== 'vip' || isVip).map((s) => {
        const count = s.id === 'mesice' ? months.length : s.id === 'kampane' ? campaigns.length : s.id === 'vip' ? vipAccounts.length : s.id === 'kontakty' ? contactCount : 0;
        return { value: s.id, label: <>{s.label}{count > 0 && <span className="klient-detail-tab-count">{count}</span>}</> };
      })} value={section} onChange={setSection} className="klient-detail-tabs" />

      <ArcPanel className="klient-detail-content">
          {state.kind === 'loading' && (
            <div className="prototype-loading" aria-label="Načítám"><span /><span /><span /><span /></div>
          )}
          {state.kind === 'error' && <div className="alert alert-danger mb-0">{state.message}</div>}
          {state.kind === 'not-ready' && <NotReadyBanner freshness={state.freshness} />}
          {state.kind === 'ready' && !state.freshness.ready && <StaleStrip freshness={state.freshness} />}

          {state.kind === 'ready' && section === 'prehled' && (
            <>
              <AffilmasterDetailMetricSection title={months[0] ? `Aktuální měsíc · ${monthLabel(months[0].ym)}` : 'Aktuální měsíc'} row={months[0] ?? null} currency={currency} />
              <AffilmasterDetailMetricSection title="Celkem" row={totals} currency={currency} />
              <NgrRoiCard value={heroRoi} currency={currency} />
              <DetailPerformanceChart months={months} />
            </>
          )}

          {state.kind === 'ready' && section === 'udaje' && (
            <UdajeSection papId={isManual ? '' : row.pap_affiliate_id} a={affiliate} fallbackCountry={country} socials={socials} socialsBusy={socialsLoading || socialsRefreshing} onOpenChannel={openChannel} />
          )}

          {state.kind === 'ready' && section === 'kontakty' && (
            <ContactsSection papId={row.pap_affiliate_id} person={person} a={affiliate} onPrimarySaved={() => setDetailReload((v) => v + 1)} onContactsChanged={setContactCount} />
          )}

          {state.kind === 'ready' && section === 'poznamky' && (
            <AffilmasterNotesSection
              resourceKey={`affiliate:${row.pap_affiliate_id}`}
              loadNotes={() => getAffilmasterAffiliateNotes(row.pap_affiliate_id)}
              addNote={(body) => addAffilmasterAffiliateNote(row.pap_affiliate_id, body)}
              updateNote={(noteId, body) => updateAffilmasterAffiliateNote(row.pap_affiliate_id, noteId, body)}
              deleteNote={(noteId) => deleteAffilmasterAffiliateNote(row.pap_affiliate_id, noteId)}
              placeholder="Dohody, preference nebo důležitý kontext spolupráce…"
              emptyText="Důležité informace o affiliate partnerovi budou přehledně na jednom místě."
            />
          )}

          {state.kind === 'ready' && section === 'propagace' && (
            <AffiliatePropagaceSection data={socials} loading={socialsLoading} refreshing={socialsRefreshing} error={socialsError} onRefresh={refreshSocials} focusProfileId={socialsFocus} />
          )}

          {state.kind === 'ready' && section === 'helpdesk' && (
            <AffiliateHelpdeskSection papId={row.pap_affiliate_id} mainEmail={affiliate?.email ?? null} />
          )}

          {state.kind === 'ready' && section === 'mesice' && (
            <section>
              <AffilmasterDetailSectionHeader title="Měsíční rozpad" description="Výkon affiliata po jednotlivých měsících" />
              <ArcTableFrame className="aff-table-wrap" label="Měsíční rozpad affiliate partnera">
              <div className="table-responsive-mobile">
              <table className="table table-sm align-middle mb-0 aff-detail-table affm-detail-table">
                <thead>
                  <tr>
                    <th>Měsíc</th><th className="text-end">Prokliky</th><th className="text-end">Registrace</th><th className="text-end">FTD</th>
                    <th className="text-end">Vklady</th><th className="text-end">NGR</th><th className="text-end">Odm. RS</th>
                    <th className="text-end">Odm. CPA</th><th className="text-end">NGR ROI</th><th className="text-center">Kampaně</th>
                  </tr>
                </thead>
                <tbody>
                  {months.length === 0 && <tr><td colSpan={10} className="text-center text-muted py-3">Žádná data.</td></tr>}
                  {months.map((m) => {
                    const roi = roiPct(m.ngr_volume, m.commission);
                    return (
                      <tr key={m.ym}>
                        <td><strong>{monthTableLabel(m.ym)}</strong></td>
                        <td className="text-end">{fmtN(m.clicks ?? 0)}</td>
                        <td className="text-end">{fmtN(m.registrations)}</td>
                        <td className="text-end">{fmtN(m.ftd_count)}</td>
                        <td className="text-end">{fmtMoney(m.deposits_volume, currency)}</td>
                        <td className="text-end">{fmtMoney(m.ngr_volume, currency)}</td>
                        <td className="text-end">{fmtMoney(m.commission_rs, currency)}</td>
                        <td className="text-end">{fmtMoney(m.commission_cpa, currency)}</td>
                        <td className="text-end" style={{ color: roiColorHex(m.ngr_volume, m.commission) }}>{roi ? `${roi} %` : <span className="text-muted">-</span>}</td>
                        <td className="text-center"><RowArrow title="Kampaně měsíce" onClick={() => setModal({ kind: 'month', ym: m.ym })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              </ArcTableFrame>
            </section>
          )}

          {state.kind === 'ready' && section === 'kampane' && (
            <section>
              <AffilmasterDetailSectionHeader title="Kampaně" description="Výkon affiliate partnera podle kampaní" />
              {campaigns.length === 0 ? <div className="prototype-empty-contact">Žádné kampaně.</div> : <ArcPanel as="div" className="prototype-entity-list">
                {campaigns.map((c, index) => {
                  const roi = roiPct(c.ngr_volume, c.commission);
                  const cname = c.campaign_name ?? c.campaign_id;
                  return <button type="button" className="prototype-entity-row" key={c.campaign_id} onClick={() => setModal({ kind: 'campaign', cid: c.campaign_id, cname })}>
                    <span className={`prototype-entity-mark tone-${index % 4}`}>{initials(cname).slice(0, 1)}</span>
                    <span className="prototype-entity-main"><strong>{cname}{c.is_vip && <VipBadge />}</strong><small>{c.is_vip ? c.campaign_id.split(':').pop() : c.campaign_id} · {periodLabel(c.first_day, c.last_day)}</small></span>
                    <span className="prototype-entity-stats"><small>{c.clicks ? `Prokliky ${fmtN(c.clicks)} · ` : ''}Reg. {fmtN(c.registrations)} · FTD {fmtN(c.ftd_count)} · Vklady {fmtMoney(c.deposits_volume, currency)} · NGR {fmtMoney(c.ngr_volume, currency)}</small><strong>RS {fmtMoney(c.commission_rs, currency)} · CPA {fmtMoney(c.commission_cpa, currency)} · ROI {roi ? `${roi} %` : '-'}</strong></span>
                    <span className="prototype-status is-active">Aktivní</span><span className="prototype-entity-value">{fmtMoney(c.commission, currency)}</span><span className="prototype-entity-arrow feather-icon" aria-hidden="true"><i data-feather="chevron-right" /></span>
                  </button>;
                })}
              </ArcPanel>}
            </section>
          )}
          {state.kind === 'ready' && section === 'vip' && isVip && (
            <VipNapojeniSection accounts={vipAccounts} />
          )}
          {state.kind === 'ready' && section === 'vyuctovani' && (
            <AffiliateDokladySection papId={row.pap_affiliate_id} />
          )}
      </ArcPanel>

      {modal?.kind === 'campaign' && (
        <CampaignChartModal
          campaignName={modal.cname}
          fetchChart={() => getAffilmasterAffiliateCampaignChart(row.pap_affiliate_id, modal.cid)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'month' && (
        <MonthBreakdownModal
          ym={modal.ym}
          subtitle="Rozpad affiliata za měsíc"
          fetchMonth={() => getAffilmasterAffiliateMonthBreakdown(row.pap_affiliate_id, modal.ym)}
          onClose={() => setModal(null)}
        />
      )}
      {profileForm && (
        <div className="prototype-modal-backdrop aff-editor-backdrop" onClick={() => !profileSaving && setProfileForm(null)}>
          <form
            className="aff-editor-modal aff-editor-modal-affiliate"
            role="dialog"
            aria-modal="true"
            aria-busy={profileSaving}
            aria-labelledby="aff-partner-editor-title"
            aria-describedby="aff-partner-editor-description"
            onSubmit={(e) => { e.preventDefault(); void saveProfile(); }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="aff-editor-head">
              <div>
                <div className="aff-editor-kicker"><i data-feather="users" /> Affilmaster · affiliate</div>
                <h2 id="aff-partner-editor-title">Upravit affiliate partnera</h2>
                <p id="aff-partner-editor-description">Profil, dosah a stav partnera v Platform 1.</p>
              </div>
              <button type="button" className="aff-editor-close" aria-label="Zavřít" disabled={profileSaving} onClick={() => setProfileForm(null)}><i data-feather="x" /></button>
            </header>

            <div className="aff-editor-body">
              {profileError && <div className="aff-editor-alert" role="alert"><i data-feather="alert-circle" /><span>{profileError}</span></div>}

              <div className="aff-editor-layout aff-editor-layout-affiliate">
                <section className="aff-editor-main" aria-labelledby="aff-partner-identity-heading">
                  <div className="aff-editor-section-head">
                    <h3 id="aff-partner-identity-heading">Identita partnera</h3>
                    <p>Název a web, pod kterými se partner zobrazuje v P1.</p>
                  </div>
                  <div className="aff-editor-grid">
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Zobrazovaný název</span>
                      <input className="aff-editor-control" autoFocus value={profileForm.displayName} onChange={(e)=>setProfileForm({...profileForm,displayName:e.target.value})} />
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Firma</span>
                      <input className="aff-editor-control" value={profileForm.companyName} onChange={(e)=>setProfileForm({...profileForm,companyName:e.target.value})} />
                      <small className="aff-editor-help">
                        Je-li vyplněná, zobrazuje se v seznamu místo zobrazovaného názvu. Nechte prázdné,
                        pokud má platit název výše.
                      </small>
                    </label>
                    <div className="aff-editor-field aff-editor-field-full">
                      <span>Propagační kanály</span>
                      <WebsitesEditor
                        serialized={profileForm.website}
                        onChange={(serialized) => setProfileForm({ ...profileForm, website: serialized })}
                      />
                    </div>
                  </div>
                </section>

                <aside className="aff-editor-aside aff-editor-affiliate-aside" aria-labelledby="aff-partner-reach-heading">
                  <div className="aff-editor-section-head">
                    <h3 id="aff-partner-reach-heading">Dosah a stav</h3>
                    <p>Trhy a dostupnost affiliate partnera.</p>
                  </div>
                  <div className="aff-editor-grid">
                    <label className="aff-editor-field">
                      <span>Země</span>
                      <select className="aff-editor-control" value={profileForm.country || ''} onChange={(e)=>setProfileForm({...profileForm,country:e.target.value})}>
                        <option value="">— vyberte —</option>
                        {COUNTRIES_TOP.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                        <option disabled>──────────</option>
                        {COUNTRIES_REST.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>
                    </label>
                    <label className="aff-editor-field">
                      <span>Trhy</span>
                      <input className="aff-editor-control" value={profileForm.markets} onChange={(e)=>setProfileForm({...profileForm,markets:e.target.value})} placeholder="CZ, SK" />
                      <small className="aff-editor-help">Více trhů oddělte čárkou.</small>
                    </label>
                    <label className="aff-editor-field aff-editor-field-full">
                      <span>Stav partnera</span>
                      <select className="aff-editor-control" value={profileForm.status} onChange={(e)=>setProfileForm({...profileForm,status:e.target.value})}><option value="active">Aktivní</option><option value="inactive">Pozastavený</option></select>
                    </label>
                  </div>

                  <div className="aff-editor-source-note">
                    <i data-feather="database" />
                    <div><strong>Platform 1 je hlavní zdroj</strong><p>Tyto hodnoty mají při zobrazení přednost před daty z Post Affiliate Pro.</p></div>
                  </div>
                </aside>
              </div>

              <section className="aff-editor-main" aria-labelledby="aff-partner-billing-heading" style={{ marginTop: 24 }}>
                <div className="aff-editor-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
                  <div>
                    <h3 id="aff-partner-billing-heading">Fakturační údaje</h3>
                    <p>IČO, DIČ a sídlo pro spárování s doklady.</p>
                  </div>
                  <button type="button" className="aff-editor-button is-secondary"
                    disabled={aresBusy || !profileForm.ico.replace(/\D/g,'').match(/^\d{6,8}$/)}
                    onClick={loadFromAres} title="Načte název, DIČ a adresu z ARES podle IČO (jen CZ)">
                    {aresBusy ? <><span className="aff-editor-spinner" /> Načítám…</> : 'Načíst z ARES'}
                  </button>
                </div>
                {aresMsg && <div className="aff-editor-alert" role="status" style={{ marginBottom: 8 }}><i data-feather="info" /><span>{aresMsg}</span></div>}
                <div className="aff-editor-grid">
                  <label className="aff-editor-field"><span>IČO</span>
                    <input className="aff-editor-control" value={profileForm.ico}
                      onChange={(e)=>setProfileForm({...profileForm,ico:e.target.value})} placeholder="12345678" />
                  </label>
                  <label className="aff-editor-field"><span>DIČ</span>
                    <input className="aff-editor-control" value={profileForm.dic}
                      onChange={(e)=>setProfileForm({...profileForm,dic:e.target.value})} placeholder="CZ12345678" />
                  </label>
                  <label className="aff-editor-field aff-editor-field-full"><span>Ulice a č.p.</span>
                    <input className="aff-editor-control" value={profileForm.billingStreet}
                      onChange={(e)=>setProfileForm({...profileForm,billingStreet:e.target.value})} />
                  </label>
                  <label className="aff-editor-field"><span>Město</span>
                    <input className="aff-editor-control" value={profileForm.billingCity}
                      onChange={(e)=>setProfileForm({...profileForm,billingCity:e.target.value})} />
                  </label>
                  <label className="aff-editor-field"><span>PSČ</span>
                    <input className="aff-editor-control" value={profileForm.billingZip}
                      onChange={(e)=>setProfileForm({...profileForm,billingZip:e.target.value})} placeholder="150 00" />
                  </label>
                  <label className="aff-editor-field"><span>Země</span>
                    <select className="aff-editor-control" value={profileForm.billingCountry || ''}
                      onChange={(e)=>setProfileForm({...profileForm,billingCountry:e.target.value})}>
                      <option value="">— vyberte —</option>
                      {COUNTRIES_TOP.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                      <option disabled>──────────</option>
                      {COUNTRIES_REST.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </label>
                </div>
              </section>
            </div>

            <footer className="aff-editor-footer">
              <span>Affiliate ID <code>{isManual ? 'bez PaP ID' : row.pap_affiliate_id}</code></span>
              <div>
                <button type="button" className="aff-editor-button is-secondary" disabled={profileSaving} onClick={() => setProfileForm(null)}>Zrušit</button>
                <button type="submit" className="aff-editor-button is-primary" disabled={profileSaving}>{profileSaving ? <><span className="aff-editor-spinner" /> Ukládám…</> : 'Uložit změny'}</button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </ArcPage>
  );
}

type SumRow = { registrations: number; ftd_count: number; deposits_volume: number; ngr_volume: number; commission: number; withdrawals_count: number };
function sumMonths(months: AffiliateMonthly['months']): SumRow {
  return months.reduce<SumRow>(
    (acc, m) => ({
      registrations: acc.registrations + (m.registrations ?? 0),
      ftd_count: acc.ftd_count + (m.ftd_count ?? 0),
      deposits_volume: acc.deposits_volume + (m.deposits_volume ?? 0),
      ngr_volume: acc.ngr_volume + (m.ngr_volume ?? 0),
      commission: acc.commission + (m.commission ?? 0),
      withdrawals_count: acc.withdrawals_count + (m.withdrawals_count ?? 0),
    }),
    { registrations: 0, ftd_count: 0, deposits_volume: 0, ngr_volume: 0, commission: 0, withdrawals_count: 0 },
  );
}

export function DetailPerformanceChart({ months }: { months: Array<{ ym: string; registrations: number; ftd_count: number }> }) {
  const rows = [...months].sort((a, b) => a.ym.localeCompare(b.ym)).slice(-12);
  const data = {
    labels: rows.map((row) => monthLabel(row.ym)),
    datasets: [
      { label: 'Registrace', data: rows.map((row) => row.registrations), borderColor: '#067836', backgroundColor: 'rgba(6,120,54,.12)', fill: true, tension: .32, pointRadius: 2, borderWidth: 2 },
      { label: 'FTD', data: rows.map((row) => row.ftd_count), borderColor: '#91c4a6', backgroundColor: 'transparent', fill: false, tension: .32, pointRadius: 2, borderWidth: 1.5, borderDash: [6, 4] },
    ],
  };
  const options = {
    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index' as const, intersect: false },
    plugins: { legend: { display: true, align: 'end' as const, labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, font: { size: 10 } } }, tooltip: { enabled: true } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 0 } }, y: { beginAtZero: true, grid: { color: '#eaebed' }, ticks: { font: { size: 9 } } } },
  };
  return <section className="prototype-detail-chart"><div className="prototype-panel-head"><div><h2>Vývoj výkonu</h2><p>Posledních 12 měsíců</p></div></div>{rows.length ? <div className="prototype-detail-chart-canvas"><Line data={data} options={options} /></div> : <div className="prototype-empty-contact">Žádná data pro graf.</div>}</section>;
}

const EMPTY_CONTACT = { name: '', vocative: '', role: '', email: '', phone: '' };

// Přijaté doklady partnera z UOL (vyúčtování vystavujeme my za partnera).
// Stejná stromová podoba jako doklady klienta; stav úhrady zde není - UOL pro
// přijaté doklady obdobu receivables nemá.
function AffiliateDokladySection({ papId }: { papId: string }) {
  const [rows, setRows] = useState<AffiliateInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rok, setRok] = useState<string>(String(new Date().getFullYear()));

  useEffect(() => {
    let cancelled = false;
    getAffilmasterAffiliateInvoices(papId)
      .then((d) => { if (!cancelled) setRows(d); })
      .catch(() => { if (!cancelled) setError('Načtení dokladů selhalo'); });
    return () => { cancelled = true; };
  }, [papId]);

  const roky = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) {
      const y = (r.issuedDate ?? '').slice(0, 4);
      if (y) set.add(y);
    }
    return [...set].sort().reverse();
  }, [rows]);

  // Když partner v aktuálním roce doklady nemá, ukáže se nejbližší rok, který má.
  useEffect(() => {
    if (roky.length > 0 && !roky.includes(rok)) setRok(roky[0]!);
  }, [roky, rok]);

  const skupiny = useMemo(() => {
    const map = new Map<string, AffiliateInvoice[]>();
    for (const r of rows ?? []) {
      if ((r.issuedDate ?? '').slice(0, 4) !== rok) continue;
      const ym = (r.issuedDate ?? '').slice(0, 7) || 'bez-data';
      const list = map.get(ym) ?? [];
      list.push(r);
      map.set(ym, list);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows, rok]);

  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  if (rows.length === 0) return <div className="text-muted py-3">Pro tohoto partnera nejsou v UOL žádné doklady.</div>;

  return (
    <div className="klient-doklady">
      <AffilmasterDetailSectionHeader title="Doklady" description="Přijaté doklady partnera podle měsíce" />
      <div className="doklady-filtr">
        <span className="doklady-filtr-label">Rok</span>
        <ArcTabs items={roky.map((y) => ({ value: y, label: y }))} value={rok} onChange={setRok} label="Rok dokladů" />
      </div>
      {skupiny.length === 0 && <div className="text-muted py-3">V roce {rok} nejsou žádné doklady.</div>}
      {skupiny.map(([ym, list]) => {
        const soucet = list.reduce((n, r) => n + (r.totalAmount ?? 0), 0);
        const mena = list.find((r) => r.currency)?.currency ?? null;
        const vicMen = new Set(list.map((r) => r.currency).filter(Boolean)).size > 1;
        return (
          <section className="doklady-mesic" key={ym}>
            <header className="doklady-mesic-head">
              <span className="doklady-mesic-nazev">{ym === 'bez-data' ? 'Bez data vystavení' : mesicLabel(ym)}</span>
              <span className="doklady-mesic-pocet">{list.length === 1 ? '1 doklad' : `${list.length} doklady`}</span>
              <span className="doklady-mesic-suma">{vicMen ? '' : fmtCastka(soucet, mena)}</span>
            </header>
            <ol className="doklady-vetev">
              {list.map((r) => (
                <ArcPanel as="li" className="doklady-radek" key={r.invoiceNumber ?? `${ym}-${r.issuedDate}`}>
                  <span className="doklady-cislo">{r.invoiceNumber ?? '—'}</span>
                  <span className="doklady-datum">{fmtDen(r.issuedDate)}</span>
                  <span className="doklady-splatnost">{fmtDen(r.dueDate)}</span>
                  <span className="doklady-castka">{fmtCastka(r.totalAmount, r.currency)}</span>
                </ArcPanel>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

function AffiliateHelpdeskSection({ papId, mainEmail }: { papId: string; mainEmail: string | null }) {
  const [emails, setEmails] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!papId) { setEmails([mainEmail ?? '']); return; }
    getAffilmasterContacts(papId)
      .then((rows) => { if (!cancelled) setEmails([mainEmail ?? '', ...rows.map((c) => c.email ?? '')]); })
      .catch(() => { if (!cancelled) setEmails([mainEmail ?? '']); });
    return () => { cancelled = true; };
  }, [papId, mainEmail]);
  return (
    <HelpdeskContactTickets emails={emails} />
  );
}

function ContactsSection({ papId, person, a, onPrimarySaved, onContactsChanged }: { papId: string; person: string | null; a: AffiliateUdaje | null; onPrimarySaved: () => void; onContactsChanged?: (count: number) => void }) {
  const [internal, setInternal] = useState<AffiliateContact[]>([]);
  const [groupVersion, setGroupVersion] = useState(0);
  const [groups, setGroups] = useState<MailingGroupsValue | null>(null);
  const [editOrigEmail, setEditOrigEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_CONTACT });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [primaryForm, setPrimaryForm] = useState<typeof EMPTY_CONTACT | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<typeof EMPTY_CONTACT | null>(null);

  const reload = () => {
    getAffilmasterContacts(papId)
      .then((rows) => { setInternal(rows); onContactsChanged?.(rows.length); })
      .catch(() => { setInternal([]); onContactsChanged?.(0); });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [papId]);
  useFeather([internal, showForm]);

  const hasMain = Boolean(person || a?.email || a?.phone);
  const mainName = person || (hasMain ? 'Kontaktní osoba' : null);
  const set = (k: keyof typeof EMPTY_CONTACT) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Jméno je povinné'); return; }
    setSaving(true); setErr(null);
    addAffilmasterContact(papId, {
      name: form.name.trim(), vocative: form.vocative || null, role: form.role || null,
      email: form.email || null, phone: form.phone || null,
    })
      .then(() => saveMailingGroups(null, form.email, groups))
      .then(() => { setForm({ ...EMPTY_CONTACT }); setShowForm(false); setGroups(null); setGroupVersion((v) => v + 1); reload(); })
      .catch((e2: unknown) => setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Uložení kontaktu selhalo'))
      .finally(() => setSaving(false));
  };

  const openEdit = (c: AffiliateContact) => {
    setEditId(c.id);
    setGroups(null);
    setEditOrigEmail(c.email ?? '');
    setEditForm({ name: c.name, vocative: c.vocative ?? '', role: c.role ?? '', email: c.email ?? '', phone: c.phone ?? '' });
    setErr(null);
  };
  const saveEdit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editForm?.name.trim() || editId == null) { setErr('Jméno je povinné'); return; }
    setSaving(true); setErr(null);
    try {
      await updateAffilmasterContact(papId, editId, {
        name: editForm.name.trim(), vocative: editForm.vocative || null, role: editForm.role || null,
        email: editForm.email || null, phone: editForm.phone || null,
      });
      await saveMailingGroups(editOrigEmail, editForm.email, groups);
      setEditId(null); setEditForm(null); setGroups(null); setGroupVersion((v) => v + 1); reload();
    } catch (e2) { setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Uložení kontaktu selhalo'); }
    finally { setSaving(false); }
  };
  const removeContact = async (c: AffiliateContact) => {
    if (!(await confirmDialog({ title: 'Smazat kontakt?', message: `Kontakt „${c.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    try { await deleteAffilmasterContact(papId, c.id); reload(); }
    catch (e2) { setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Smazání kontaktu selhalo'); }
  };

  const editPrimary = () => {
    setGroups(null);
    setPrimaryForm({ name: person ?? '', vocative: a?.vocative ?? '', role: 'Hlavní', email: a?.email ?? '', phone: a?.phone ?? '' });
  };
  const savePrimary = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!primaryForm?.name.trim()) { setErr('Jméno je povinné'); return; }
    setSaving(true); setErr(null);
    try {
      await updateAffilmasterPrimaryContact(papId, {
        name: primaryForm.name.trim(), vocative: primaryForm.vocative || null, role: primaryForm.role || null,
        email: primaryForm.email || null, phone: primaryForm.phone || null,
      });
      await saveMailingGroups(a?.email ?? '', primaryForm.email, groups);
      setPrimaryForm(null); setGroups(null); setGroupVersion((v) => v + 1); onPrimarySaved();
    } catch (e2) { setErr(e2 instanceof AffilmasterApiError ? e2.message : 'Uložení kontaktu selhalo'); }
    finally { setSaving(false); }
  };

  return (
    <div className="prototype-contacts-view">
      <AffilmasterDetailSectionHeader title="Kontakty" description={`${internal.length} ${internal.length === 1 ? 'kontaktní osoba' : internal.length >= 2 && internal.length <= 4 ? 'kontaktní osoby' : 'kontaktních osob'} affiliate partnera`} action={<Button type="button" size="sm" onClick={() => { setGroups(null); setShowForm((v) => !v); }}>
          <i data-feather="plus" /> Přidat kontakt
        </Button>} />

      {showForm && (
        <form className="card card-body bg-body-tertiary mb-3" onSubmit={submit}>
          <div className="row g-2">
            <div className="col-md-3"><input className="form-control form-control-sm" placeholder="Jméno *" value={form.name} onChange={set('name')} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Oslovit" value={form.vocative} onChange={set('vocative')} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Role" value={form.role} onChange={set('role')} /></div>
            <div className="col-md-3"><input className="form-control form-control-sm" type="email" placeholder="Email" value={form.email} onChange={set('email')} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Telefon" value={form.phone} onChange={set('phone')} /></div>
            <div className="col-12"><MailingGroupsField email={null} currentEmail={form.email} value={groups} onChange={setGroups} /></div>
          </div>
          {err && <div className="text-danger small mt-2">{err}</div>}
          <div className="mt-2 d-flex gap-2">
            <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>{saving ? 'Ukládám…' : 'Uložit kontakt'}</button>
            <button type="button" className="btn btn-sm btn-link text-muted" onClick={() => { setShowForm(false); setErr(null); }}>Zrušit</button>
          </div>
        </form>
      )}

      {internal.length === 0 ? (
        <div className="prototype-empty-contact">Žádný kontakt. Přidej první přes „Přidat kontakt“.</div>
      ) : <AffilmasterContactDirectory
        key={groupVersion}
        label="Kontaktní osoby affiliate partnera"
        contacts={internal.map((c, idx) => ({
          id: c.id,
          name: c.name,
          role: idx === 0 ? 'Hlavní kontakt' : (c.role || 'Interní kontakt'),
          vocative: c.vocative,
          email: c.email,
          phone: c.phone,
        }))}
        onEdit={(contact: AffilmasterContactDirectoryItem) => {
          const source = internal.find((row) => row.id === contact.id);
          if (source) openEdit(source);
        }}
        onDelete={(contact: AffilmasterContactDirectoryItem) => {
          const source = internal.find((row) => row.id === contact.id);
          if (source) void removeContact(source);
        }}
      />
      }
      {editForm && <div className="prototype-modal-backdrop" onClick={() => { setEditForm(null); setEditId(null); }}>
        <form className="card prototype-modal-card prototype-entity-modal" onSubmit={saveEdit} onClick={(e) => e.stopPropagation()}>
          <div className="prototype-modal-head"><div><div className="prototype-eyebrow">KONTAKT</div><h2>Upravit kontakt</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => { setEditForm(null); setEditId(null); }}>×</button></div>
          <div className="card-body prototype-entity-form">
            {err && <div className="prototype-form-error full">{err}</div>}
            <div className="prototype-field full"><label>Jméno a příjmení *</label><input className="form-control" autoFocus value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="prototype-field"><label>Oslovení</label><input className="form-control" value={editForm.vocative} onChange={(e) => setEditForm({ ...editForm, vocative: e.target.value })} /></div>
            <div className="prototype-field"><label>Role</label><input className="form-control" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} /></div>
            <div className="prototype-field"><label>E-mail</label><input className="form-control" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="prototype-field"><label>Telefon</label><input className="form-control" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <MailingGroupsField email={editOrigEmail} currentEmail={editForm.email} value={groups} onChange={setGroups} />
          </div>
          <div className="card-footer prototype-modal-actions"><button type="button" className="btn prototype-secondary-btn" onClick={() => { setEditForm(null); setEditId(null); }}>Zrušit</button><button type="submit" className="btn btn-primary prototype-primary-btn" disabled={saving}>{saving ? 'Ukládám…' : 'Uložit změny'}</button></div>
        </form>
      </div>}
      {primaryForm && <div className="prototype-modal-backdrop" onClick={() => setPrimaryForm(null)}>
        <form className="card prototype-modal-card prototype-entity-modal" onSubmit={savePrimary} onClick={(e) => e.stopPropagation()}>
          <div className="prototype-modal-head"><div><div className="prototype-eyebrow">HLAVNÍ KONTAKT</div><h2>Upravit kontaktní osobu</h2><p>Úprava v P1 bude mít přednost před údaji z PaP.</p></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => setPrimaryForm(null)}>×</button></div>
          <div className="card-body prototype-entity-form">
            {err && <div className="prototype-form-error full">{err}</div>}
            <div className="prototype-field full"><label>Jméno a příjmení *</label><input className="form-control" autoFocus value={primaryForm.name} onChange={(e) => setPrimaryForm({ ...primaryForm, name: e.target.value })} /></div>
            <div className="prototype-field"><label>Oslovení</label><input className="form-control" value={primaryForm.vocative} onChange={(e) => setPrimaryForm({ ...primaryForm, vocative: e.target.value })} /></div>
            <div className="prototype-field"><label>Role</label><input className="form-control" value={primaryForm.role} onChange={(e) => setPrimaryForm({ ...primaryForm, role: e.target.value })} /></div>
            <div className="prototype-field"><label>E-mail</label><input className="form-control" type="email" value={primaryForm.email} onChange={(e) => setPrimaryForm({ ...primaryForm, email: e.target.value })} /></div>
            <div className="prototype-field"><label>Telefon</label><input className="form-control" value={primaryForm.phone} onChange={(e) => setPrimaryForm({ ...primaryForm, phone: e.target.value })} /></div>
            <MailingGroupsField email={a?.email ?? ''} currentEmail={primaryForm.email} value={groups} onChange={setGroups} />
          </div>
          <div className="card-footer prototype-modal-actions"><button type="button" className="btn prototype-secondary-btn" onClick={() => setPrimaryForm(null)}>Zrušit</button><button type="submit" className="btn btn-primary prototype-primary-btn" disabled={saving}>{saving ? 'Ukládám…' : 'Uložit změny'}</button></div>
        </form>
      </div>}
    </div>
  );
}

function WebsitesEditor({ serialized, onChange }: { serialized: string; onChange: (serialized: string) => void }) {
  const [list, setList] = useState<WebEntry[]>(() => {
    const parsed = parseWebsites(serialized);
    return parsed.length ? parsed : [{ kind: 'web', url: '' }];
  });
  useFeather([list]);
  const flush = (next: WebEntry[]) => {
    setList(next);
    onChange(serializeWebsites(next));
  };
  const update = (idx: number, patch: Partial<WebEntry>) => flush(list.map((e, i) => i === idx ? { ...e, ...patch } : e));
  const add = () => flush([...list, { kind: 'web', url: '' }]);
  const remove = (idx: number) => {
    const next = list.filter((_, i) => i !== idx);
    flush(next.length ? next : [{ kind: 'web', url: '' }]);
  };
  const canAdd = list.length < MAX_WEBSITES;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {list.map((entry, idx) => {
        const meta = WEB_KIND_MAP[entry.kind] ?? WEB_KIND_MAP.web;
        return (
          <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 40, padding: '0 8px 0 10px', border: '1px solid #e3e5e7', borderRadius: 8, background: '#fff', flex: '0 0 auto' }}>
              <WebKindIcon kind={entry.kind} size={15} />
              <select value={entry.kind}
                onChange={(e) => update(idx, { kind: e.target.value as WebKind })}
                style={{ border: 0, outline: 0, background: 'transparent', fontSize: 12, fontWeight: 500, cursor: 'pointer', paddingRight: 12 }}
                aria-label="Typ odkazu">
                {WEB_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
              </select>
            </span>
            <input className="aff-editor-control" value={entry.url}
              onChange={(e) => update(idx, { url: e.target.value })}
              placeholder={idx === 0 ? 'www.example.com (hlavní)' : 'www.example.com'}
              style={{ flex: 1 }} />
            {idx > 0 && (
              <button type="button" onClick={() => remove(idx)} title="Odebrat"
                style={{ width: 30, height: 30, border: '1px solid #e3e5e7', borderRadius: 8, background: '#fff', color: '#a1a7ae', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
            )}
          </div>
        );
      })}
      {canAdd && (
        <button type="button" onClick={add}
          style={{ alignSelf: 'flex-start', border: 0, background: 'transparent', color: '#737b81', fontSize: 12, fontWeight: 500, padding: '2px 4px', cursor: 'pointer' }}>
          + přidat další ({list.length}/{MAX_WEBSITES})
        </button>
      )}
    </div>
  );
}

// Zahraniční účty přicházejí jako jedna dlouhá věta ("Beneficiary name: …,
// IBAN: …, Bank address: …"). Rozpad na řádky po částech je v dlaždici čitelný;
// krátké tuzemské číslo účtu zůstává na jednom řádku.
function splitBankAccount(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim().replace(/,\s*$/, '');
  if (t.length <= 40) return t;
  // Zalomit jen před popiskem dalšího pole, ne u každé čárky - "Fio banka, a. s."
  // je jeden údaj a rozpadlý na dva řádky by mátl.
  return t.replace(/,\s*(?=(?:Beneficiary|IBAN|Bank|SWIFT|BIC|Account|Address|Recipient|Payee)\b)/gi, '\n');
}

const VAT_TARIFF_LABEL: Record<string, string> = {
  tax: 'S daní',
  notax: 'Bez daně',
  reverse: 'Reverse charge',
};

function vatTariffLabel(v: string | null | undefined): string | null {
  if (!v) return null;
  return VAT_TARIFF_LABEL[v.toLowerCase()] ?? v;
}

function formatMinPayout(v: number | null | undefined, currency: string | null | undefined): string | null {
  if (v == null) return null;
  const mena = (currency ?? '').includes('EUR') && !(currency ?? '').includes('CZK') ? 'EUR' : 'CZK';
  return `${v.toLocaleString('cs-CZ')} ${mena}`;
}

function PapPairingCard({ manualId, fromCrm, onPaired }: { manualId: string; fromCrm: boolean; onPaired?: (papAffiliateId: string) => void }) {
  const [papId, setPapId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = papId.trim();
    if (!value) { setError('Zadejte PaP ID.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await pairAffilmasterAffiliateWithPap(manualId, value);
      onPaired?.(res.papAffiliateId);
    } catch (err: unknown) {
      setError(err instanceof AffilmasterApiError ? err.message : 'Spárování selhalo');
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="aff-pap-pair" onSubmit={submit}>
      <div className="aff-pap-pair-copy">
        <strong>Spárujte partnera s affiliate platformou</strong>
        <p>{fromCrm ? 'Partner vznikl z CRM leadu a zatím nemá PaP ID.' : 'Partner byl přidaný ručně a zatím nemá PaP ID.'} Zadejte jeho PaP ID z affiliate platformy, aby se napojily výkony, kampaně a vyúčtování.</p>
        {error && <span className="aff-pap-pair-error">{error}</span>}
      </div>
      <div className="aff-pap-pair-field">
        <input className="form-control" value={papId} onChange={(e) => setPapId(e.target.value)} placeholder="PaP ID, např. 14873d5a" aria-label="PaP ID" disabled={saving} />
        <button type="submit" className="btn btn-primary" disabled={saving || !papId.trim()}>{saving ? 'Ověřuji…' : 'Spárovat'}</button>
      </div>
    </form>
  );
}

export function VipBadge({ title }: { title?: string }) {
  return <span className="badge badge-vip" title={title ?? 'VIP partner, vlastní účet v PaP instanci kasina'}>VIP</span>;
}

const VIP_CLASSIFICATION: Record<string, string> = {
  explicit_paid_assignment: 'Konkrétní placené přiřazení',
  default_with_commission_history: 'Výchozí sazba s historií provizí',
  historical_nonzero_current_zero: 'Historické provize, nyní 0 %',
  unconfirmed_default: 'Nepotvrzený výchozí profil',
  not_in_campaign: 'Mimo kampaň',
  unverified: 'Neověřeno',
};
const VIP_MEMBERSHIP: Record<string, string> = { A: 'Schválené', R: 'Ascending', F: 'Descending', X: 'Fixní' };

function VipNapojeniSection({ accounts }: { accounts: VipAccount[] }) {
  const pct = (v: number | null) => (v == null ? '-' : `${(v * 100).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} %`);
  return (
    <section>
      <AffilmasterDetailSectionHeader title="VIP napojení" description="Účty partnera přímo v PaP instancích kasin a jejich provizní podmínky" />
      {accounts.length === 0 ? <div className="prototype-empty-contact">Žádné napojené instance.</div> : (
        <ArcTableFrame className="aff-table-wrap vip-table-scroll" label="VIP napojení affiliate partnera" sticky="container">
          <div className="table-responsive-mobile">
            <table className="table table-sm align-middle mb-0 aff-detail-table affm-detail-table">
              <thead>
                <tr><th>Kasino</th><th>Účet ve zdroji</th><th>Referral ID</th><th>Kampaň</th><th>Provizní skupina</th><th className="text-end">per Sale</th><th>Členství</th><th>Zařazení</th><th>Účtovat klientovi</th><th>Čteno</th></tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.casino}</strong></td>
                    <td>
                      <div>{a.sourceName ?? '-'}</div>
                      <small className="text-muted">ID <code>{a.localAffiliateId}</code>{a.websiteInSource ? ` · ${a.websiteInSource.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : ''}</small>
                      {a.note && <div><small className="text-muted">{a.note}</small></div>}
                    </td>
                    <td><code>{a.referralId ?? '-'}</code></td>
                    <td><code>{a.campaignId}</code></td>
                    <td>{a.commissionGroupName ?? '-'}{a.otherCommissions && <div><small className="text-muted">{a.otherCommissions}</small></div>}</td>
                    <td className="text-end"><strong>{pct(a.perSaleRate)}</strong></td>
                    <td>{a.membershipStatus ? `${VIP_MEMBERSHIP[a.membershipStatus] ?? a.membershipStatus} (${a.membershipStatus})` : a.assignmentKind === 'inherited_default' ? 'Výchozí (zděděné)' : '-'}</td>
                    <td><small>{VIP_CLASSIFICATION[a.classification ?? ''] ?? a.classification ?? '-'}</small></td>
                    <td>{a.vipBilling ? <span className="badge badge-soft-success">Ano</span> : <span className="badge badge-soft-secondary">Ne</span>}</td>
                    <td><small className="text-muted">{czDate(a.sourceObservedAt)}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ArcTableFrame>
      )}
    </section>
  );
}

function UdajeSection({ papId, a, fallbackCountry, socials, socialsBusy, onOpenChannel }: { papId: string; a: AffiliateUdaje | null; fallbackCountry: string | null; socials: AffiliateSocials | null; socialsBusy: boolean; onOpenChannel: (profileId: number | null) => void }) {
  const country = a?.country ?? fallbackCountry;
  const websites = parseWebsites(a?.website);
  const channelKey = (url: string) => url.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  const profileFor = (url: string) => socials?.profiles.find((p) => channelKey(p.url) === channelKey(url)) ?? null;
  return (
    <div className="aff-detail-fields-layout">
      <AffilmasterDetailFieldGroup title="Identifikace">
        <AffilmasterDetailField label="Název firmy" value={a?.company_name} />
        <AffilmasterDetailField label="Zobrazovaný název" value={a?.display_name} />
        <AffilmasterDetailField label="PaP ID" code={papId || undefined} />
        <AffilmasterDetailField label="IČO" value={a?.ico} />
        <AffilmasterDetailField label="DIČ" value={a?.dic} />
        <AffilmasterDetailField label="Země" value={countryName(country)} />
      </AffilmasterDetailFieldGroup>
      <AffilmasterDetailFieldGroup title="Adresa sídla">
        <AffilmasterDetailField label="Ulice a č.p." value={a?.street} />
        <AffilmasterDetailField label="Město" value={a?.city} />
        <AffilmasterDetailField label="PSČ" value={a?.zipcode} />
      </AffilmasterDetailFieldGroup>
      <AffilmasterDetailFieldGroup title="Kontakt">
        <AffilmasterDetailField label="Kontaktní osoba" value={a?.contact_person} />
        <AffilmasterDetailField label="Hlavní e-mail" link={a?.email ? `mailto:${a.email}` : null} value={a?.email} prefix={<MailStatusMark email={a?.email} />} />
        <AffilmasterDetailField label="Telefon" link={a?.phone ? `tel:${a.phone}` : null} value={a?.phone} />
      </AffilmasterDetailFieldGroup>
      <AffilmasterDetailFieldGroup title="Fakturace a výplata">
        <AffilmasterDetailField label="Měna fakturace" value={a?.billing_currency} />
        <AffilmasterDetailField label="Měna bankovního účtu" value={a?.payout_currency} />
        <AffilmasterDetailField label="Bankovní účet CZK" value={splitBankAccount(a?.bank_account_czk)} />
        <AffilmasterDetailField label="Bankovní účet EUR" value={splitBankAccount(a?.bank_account_eur)} />
        <AffilmasterDetailField label="Jazyk dokladů" value={a?.invoice_language} />
        <AffilmasterDetailField label="Daňový režim" value={vatTariffLabel(a?.vat_tariff)} />
        <AffilmasterDetailField label="Minimální výplata" value={formatMinPayout(a?.min_payout, a?.billing_currency)} />
      </AffilmasterDetailFieldGroup>
      <section className="udaje-section">
        <AffilmasterDetailSectionHeader title="Propagační kanály" />
          <div className="udaje-channel-grid">
            {websites.length === 0 && (
              <div className="udaje-channel-empty">
                <span className="udaje-channel-icon"><WebKindIcon kind="web" size={20} /></span>
                <div><strong>Bez propagačních kanálů</strong><small>Partner zatím nemá přidaný web ani sociální síť.</small></div>
              </div>
            )}
            {websites.map((entry, idx) => {
              const prof = profileFor(entry.url);
              const platform = WEB_KIND_MAP[entry.kind]?.label ?? (idx === 0 ? 'Hlavní web' : `Kanál ${idx + 1}`);
              const parsed = webPlatform(entry.url, entry.kind);
              const identity = prof?.platform === 'web'
                ? (parsed?.name || parsed?.full || entry.url)
                : (prof?.title || (prof?.handle ? `@${prof.handle}` : parsed?.full || entry.url));
              const metricValue = prof?.primary.value != null ? fmtSocialValue(prof.primary.value) : '—';
              const metricLabel = prof?.primary.value != null
                ? prof.primary.label
                : prof?.lastError
                  ? 'Data nejsou dostupná'
                  : socialsBusy
                    ? 'Načítám data…'
                    : 'Zatím bez dat';
              return (
                <article className={`udaje-channel-card is-${entry.kind}`} key={`${entry.kind}-${entry.url}`}>
                  <button type="button" className="udaje-channel-open" title="Otevřít detail v záložce Propagace" onClick={() => onOpenChannel(prof?.id ?? null)}>
                    <span className="udaje-channel-head">
                      <UdajeChannelIcon profile={prof} kind={entry.kind} />
                      <span className="udaje-channel-identity"><small>{platform}</small><strong>{identity}</strong></span>
                    </span>
                    <span className={`udaje-channel-metric${prof?.primary.value == null ? ' is-empty' : ''}`}>
                      <strong>{metricValue}</strong>
                      <small>{metricLabel}</small>
                    </span>
                  </button>
                  {parsed && (
                    <a className="udaje-channel-external" href={parsed.href} target="_blank" rel="noreferrer" title={parsed.full}>
                      <span>{parsed.full}</span>
                    </a>
                  )}
                </article>
              );
            })}
          </div>
      </section>
    </div>
  );
}

function UdajeChannelIcon({ profile, kind }: { profile: AffiliateSocialProfile | null; kind: WebKind }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = profile?.platform === 'web' ? profile.faviconUrl : profile?.avatarUrl;
  if (imageUrl && !imageFailed) {
    return (
      <span className={`udaje-channel-icon${profile?.platform === 'web' ? ' is-favicon' : ' is-avatar'}`}>
        <img src={imageUrl} alt="" onError={() => setImageFailed(true)} />
      </span>
    );
  }
  return <span className="udaje-channel-icon"><WebKindIcon kind={kind} size={20} /></span>;
}

function RoiBar({ label, value, color, pct }: { label: string; value: string; color: string; pct?: number }) {
  const w = pct == null ? 100 : Math.min(100, Math.max(4, pct));
  return (
    <div className="aff-roi-bar">
      <div className="aff-roi-bar-head">
        <span className="aff-roi-bar-label">{label}</span>
        <span className="aff-roi-bar-value">{value}</span>
      </div>
      <div className="aff-roi-bar-track"><div className="aff-roi-bar-fill" style={{ width: `${w}%`, background: color }} /></div>
    </div>
  );
}
